import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Calendar, Car, CheckCircle2, Clock, FileText, Loader2, MessageSquare, Package, Receipt, RefreshCw, Sparkles, Wrench, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, ErrorBanner, KpiTile, Loader, Money, PageHeader, Section, StatusBadge } from "@/components/common";
import { fmtDateTime, fmtMinutes, fmtRelative, fmtTime, fmtDate } from "@/lib/format";
import { FINANCE } from "@/lib/i18n";

function Row({ onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/60">
      {children}
    </button>
  );
}

function RoRow({ ro, extra, onClick }) {
  return (
    <Row onClick={onClick} testId={`dash-ro-${ro.number}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 font-semibold"><span className="tabular text-primary">{ro.number}</span><span className="truncate">{ro.vehicle_label}</span></div>
        <div className="truncate text-xs text-muted-foreground">{ro.client_name}{extra ? ` · ${extra}` : ""}</div>
      </div>
      <StatusBadge status={ro.status} />
    </Row>
  );
}

function SetupCard({ setup }) {
  const navigate = useNavigate();
  const steps = [
    { key: "workshop", done: setup.workshop_complete, title: "Coordonnées de l'atelier", text: "Nom, adresse, n° de TVA, téléphone et horaires — repris sur les documents de réception, devis et factures.", to: "/parametres?tab=atelier", cta: "Compléter" },
    { key: "team", done: setup.team > 0, title: "Équipe & rôles", text: "Créez vos collaborateurs (réception, mécaniciens avec compétences, pièces, comptable) avec leur mot de passe.", to: "/parametres?tab=equipe", cta: "Ajouter un collaborateur" },
    { key: "client", done: setup.clients > 0 && setup.vehicles_linked > 0, title: "Premier client & véhicule", text: setup.clients > 0 ? "Un client existe : ajoutez-lui maintenant son véhicule (ou scannez la carte grise) pour ouvrir votre premier dossier." : "Créez une fiche client puis son véhicule (ou scannez la carte grise) pour ouvrir votre premier dossier.", to: setup.clients > 0 ? "/vehicules" : "/clients", cta: setup.clients > 0 ? "Ajouter un véhicule" : "Créer un client" },
  ];
  const remaining = steps.filter((s) => !s.done);
  if (!remaining.length) return null;
  return (
    <Section title="Mise en route" className="rise border-primary/30" testId="dashboard-setup-card" action={<span className="text-xs text-muted-foreground">{steps.length - remaining.length}/{steps.length} étapes terminées</span>}>
      <div className="grid gap-3 md:grid-cols-3">
        {steps.map((s) => (
          <div key={s.key} className={`rounded-xl border p-3 ${s.done ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-card"}`} data-testid={`setup-step-${s.key}`}>
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold">{s.done ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <span className="grid h-4 w-4 place-items-center rounded-full border border-primary text-[10px] text-primary">{steps.indexOf(s) + 1}</span>}{s.title}</div>
            <p className="mb-2 text-xs text-muted-foreground">{s.text}</p>
            {!s.done && <Button size="sm" variant="outline" onClick={() => navigate(s.to)} className="rounded-full" data-testid={`setup-step-${s.key}-button`}>{s.cta}</Button>}
          </div>
        ))}
      </div>
    </Section>
  );
}


export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, loading, error, reload, setData } = useFetch(() => api.get("/dashboard"), []);
  const [generating, setGenerating] = useState(false);
  const finance = FINANCE.includes(user.role);

  const regenerate = async () => {
    setGenerating(true);
    try {
      const { data: s } = await api.post("/dashboard/summary");
      setData((d) => ({ ...d, ai_summary: s }));
      toast.success("Synthèse quotidienne mise à jour");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setGenerating(false);
    }
  };

  if (loading && !data) return <Loader label="Chargement du tableau de bord…" />;
  if (error) return <ErrorBanner message={error} onRetry={reload} />;
  const k = data.kpis;
  const go = (id) => navigate(`/atelier/${id}`);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHeader title="Tableau de bord" subtitle={`${fmtDate(new Date())} · Vue temps réel de l'atelier`} actions={<Button variant="outline" onClick={reload} className="rounded-full" data-testid="dashboard-refresh-button"><RefreshCw className="mr-2 h-4 w-4" /> Actualiser</Button>} />

      {data.setup && ["admin", "workshop_manager"].includes(user.role) && <SetupCard setup={data.setup} />}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
        <KpiTile label="RDV du jour" value={k.appointments_today} icon={Calendar} onClick={() => navigate("/planning")} testId="kpi-appointments" />
        <KpiTile label="Présents" value={k.vehicles_present} icon={Car} onClick={() => navigate("/atelier")} testId="kpi-present" delay={40} />
        <KpiTile label="En réparation" value={k.in_repair} icon={Wrench} tone="info" onClick={() => navigate("/atelier?status=in_repair")} testId="kpi-in-repair" delay={80} />
        <KpiTile label="Bloqués" value={k.blocked} icon={AlertTriangle} tone={k.blocked ? "warning" : "default"} testId="kpi-blocked" delay={120} />
        <KpiTile label="Dépassements" value={k.overtime} icon={Clock} tone={k.overtime ? "danger" : "default"} testId="kpi-overtime" delay={160} />
        <KpiTile label="Prêts" value={k.ready} icon={CheckCircle2} tone={k.ready ? "success" : "default"} onClick={() => navigate("/atelier?status=ready")} testId="kpi-ready" delay={200} />
        <KpiTile label="Devis en attente" value={k.quotes_pending} icon={FileText} onClick={() => navigate("/devis?status=sent")} testId="kpi-quotes" delay={240} />
        {finance ? <KpiTile label="Impayés" value={<Money value={k.unpaid_total} />} hint={`${k.unpaid_count} facture(s)`} icon={Receipt} tone={k.unpaid_count ? "warning" : "default"} onClick={() => navigate("/factures")} testId="kpi-unpaid" delay={280} /> : <KpiTile label="Messages" value={k.messages} icon={MessageSquare} onClick={() => navigate("/messages")} testId="kpi-messages" delay={280} />}
      </div>

      <section className="surface relative overflow-hidden p-5 rise rise-2" data-testid="ai-summary-card">
        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/15 blur-3xl" />
        <div className="relative flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><span className="eyebrow !text-primary">Synthèse quotidienne IA</span>{data.ai_summary && <span className="text-xs text-muted-foreground">· générée {fmtRelative(data.ai_summary.generated_at)}</span>}</div>
            {data.ai_summary ? <p className="text-sm leading-relaxed" data-testid="ai-summary-text">{data.ai_summary.text}</p> : <p className="text-sm text-muted-foreground">Aucune synthèse générée aujourd'hui. Lancez-la pour obtenir un résumé fondé uniquement sur les données réelles de l'atelier.</p>}
            {data.ai_summary?.sources?.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5">{[...new Set(data.ai_summary.sources)].map((s) => <button key={s} onClick={() => { const ro = [...data.blocked, ...data.late_risk, ...data.overtime, ...data.ready].find((r) => r.number === s); ro && go(ro.id); }} className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary hover:bg-primary/20" data-testid="ai-summary-source">{s}</button>)}</div>}
          </div>
          <Button onClick={regenerate} disabled={generating} className="shrink-0 rounded-full" data-testid="ai-summary-generate-button">{generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}{data.ai_summary ? "Régénérer" : "Générer la synthèse"}</Button>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <Section title="Rendez-vous du jour" count={data.appointments_today.length} testId="section-appointments" className="rise rise-1">
          {data.appointments_today.length === 0 ? <EmptyState title="Aucun rendez-vous aujourd'hui" /> : data.appointments_today.map((a) => (
            <Row key={a.id} onClick={() => navigate("/planning")} testId={`dash-appt-${a.id}`}>
              <div className="min-w-0"><div className="truncate font-semibold">{fmtTime(a.start)} · {a.title}</div><div className="truncate text-xs text-muted-foreground">{a.mechanic_name || "Non attribué"} · {a.bay_id || "—"}</div></div>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${a.status === "arrived" ? "bg-emerald-500/15 text-emerald-500" : "bg-blue-500/15 text-blue-500"}`}>{a.status === "arrived" ? "Arrivé" : "Attendu"}</span>
            </Row>
          ))}
        </Section>

        <Section title="Réparations en cours par mécanicien" count={Object.values(data.in_progress_by_mechanic).flat().length} testId="section-in-progress" className="rise rise-2">
          {Object.keys(data.in_progress_by_mechanic).length === 0 ? <EmptyState title="Aucune tâche en cours" /> : Object.entries(data.in_progress_by_mechanic).map(([mech, tasks]) => (
            <div key={mech} className="mb-2">
              <div className="eyebrow mb-1 px-3">{mech}</div>
              {tasks.map((t, i) => (
                <Row key={i} onClick={() => go(t.ro_id)} testId={`dash-task-${t.ro_number}`}>
                  <div className="min-w-0"><div className="truncate font-medium">{t.task}</div><div className="truncate text-xs text-muted-foreground">{t.ro_number} · {t.vehicle_label}</div></div>
                  <span className={`tabular text-xs font-semibold ${t.overtime ? "text-red-500" : "text-muted-foreground"}`}>{fmtMinutes(t.work_minutes)} / {fmtMinutes(t.estimated_minutes)}</span>
                </Row>
              ))}
            </div>
          ))}
        </Section>

        <Section title="Véhicules immobilisés" count={data.blocked.length} testId="section-blocked" className="rise rise-3">
          {data.blocked.length === 0 ? <EmptyState title="Aucun blocage" /> : data.blocked.map((ro) => <RoRow key={ro.id} ro={ro} extra={ro.blocked_reason} onClick={() => go(ro.id)} />)}
        </Section>

        <Section title="Travaux dépassant le temps prévu" count={data.overtime.length} testId="section-overtime" className="rise rise-2">
          {data.overtime.length === 0 ? <EmptyState title="Aucun dépassement" /> : data.overtime.map((ro) => <RoRow key={ro.id} ro={ro} extra={`${fmtMinutes(ro.flags.work_minutes)} réalisées / ${fmtMinutes(ro.flags.estimated_minutes)} prévues`} onClick={() => go(ro.id)} />)}
        </Section>

        <Section title="Risque de retard de restitution" count={data.late_risk.length} testId="section-late" className="rise rise-3">
          {data.late_risk.length === 0 ? <EmptyState title="Aucun risque identifié" /> : data.late_risk.map((ro) => <RoRow key={ro.id} ro={ro} extra={`Promis ${fmtDateTime(ro.promised_date)}`} onClick={() => go(ro.id)} />)}
        </Section>

        <Section title="Véhicules prêts à restituer" count={data.ready.length} testId="section-ready" className="rise rise-4">
          {data.ready.length === 0 ? <EmptyState title="Aucun véhicule prêt" /> : data.ready.map((ro) => <RoRow key={ro.id} ro={ro} extra={ro.ready_at ? `Prêt ${fmtRelative(ro.ready_at)}` : ""} onClick={() => go(ro.id)} />)}
        </Section>

        <Section title="Devis en attente de réponse" count={data.quotes_pending.length} testId="section-quotes" className="rise rise-3">
          {data.quotes_pending.length === 0 ? <EmptyState title="Aucun devis en attente" /> : data.quotes_pending.map((q) => (
            <Row key={q.id} onClick={() => navigate(`/devis/detail/${q.id}`)} testId={`dash-quote-${q.number}`}>
              <div className="min-w-0"><div className="font-semibold"><span className="tabular text-primary">{q.number}</span> · {q.client_name}</div><div className="text-xs text-muted-foreground">Envoyé {fmtRelative(q.sent_at)}</div></div>
              {q.total_ttc !== undefined && <Money value={q.total_ttc} className="text-sm font-semibold" />}
            </Row>
          ))}
        </Section>

        <Section title="Pièces commandées · livraisons attendues" count={data.parts_ordered.length} testId="section-parts" className="rise rise-4">
          {data.parts_ordered.length === 0 ? <EmptyState icon={Package} title="Aucune commande en cours" /> : data.parts_ordered.map((o) => (
            <Row key={o.id} onClick={() => navigate("/pieces?tab=commandes")} testId={`dash-po-${o.number}`}>
              <div className="min-w-0"><div className="font-semibold"><span className="tabular">{o.number}</span> · {o.supplier_name}</div><div className="text-xs text-muted-foreground">{o.lines.length} ligne(s){o.ro_number ? ` · pour ${o.ro_number}` : ""}</div></div>
              <span className="text-xs text-muted-foreground">{o.expected_date ? fmtDateTime(o.expected_date) : "Date inconnue"}</span>
            </Row>
          ))}
        </Section>

        {finance && (
          <Section title="Factures impayées" count={data.unpaid_invoices.length} testId="section-unpaid" className="rise rise-5">
            {data.unpaid_invoices.length === 0 ? <EmptyState title="Tout est encaissé" /> : data.unpaid_invoices.map((i) => (
              <Row key={i.id} onClick={() => navigate(`/factures?open=${i.id}`)} testId={`dash-invoice-${i.number}`}>
                <div className="min-w-0"><div className="font-semibold"><span className="tabular">{i.number}</span> · {i.client_name}</div><div className="text-xs text-muted-foreground">Échéance {fmtDate(i.due_date)}{i.status === "overdue" ? " · EN RETARD" : ""}</div></div>
                <Money value={i.balance} className={`text-sm font-semibold ${i.status === "overdue" ? "text-red-500" : ""}`} />
              </Row>
            ))}
          </Section>
        )}

        <Section title="Erreurs de synchronisation" count={data.sync_errors.length + data.carpass_errors.length} testId="section-sync" className="rise rise-5">
          {data.sync_errors.length + data.carpass_errors.length === 0 ? <EmptyState icon={WifiOff} title="Aucune erreur Billit ou Car-Pass" /> : (
            <>
              {data.sync_errors.map((i) => (
                <Row key={i.id} onClick={() => navigate(`/factures?open=${i.id}`)} testId={`dash-sync-${i.number}`}>
                  <div className="min-w-0"><div className="font-semibold text-red-500">Billit · {i.number}</div><div className="truncate text-xs text-muted-foreground">{i.error}</div></div>
                </Row>
              ))}
              {data.carpass_errors.map((c, idx) => (
                <Row key={idx} onClick={() => navigate(`/vehicules/${c.vehicle_id}`)} testId={`dash-carpass-${c.plate}`}>
                  <div className="min-w-0"><div className="font-semibold text-red-500">Car-Pass · {c.plate}</div><div className="truncate text-xs text-muted-foreground">{c.note}</div></div>
                </Row>
              ))}
            </>
          )}
        </Section>

        <Section title="Messages clients à traiter" count={data.messages_to_answer.length} testId="section-messages" className="rise rise-5">
          {data.messages_to_answer.length === 0 ? <EmptyState icon={MessageSquare} title="Aucun message en attente" /> : data.messages_to_answer.map((m) => (
            <Row key={m.id} onClick={() => navigate(`/messages?client=${m.client_id}`)} testId={`dash-message-${m.id}`}>
              <div className="min-w-0"><div className="font-semibold">{m.client_name}</div><div className="truncate text-xs text-muted-foreground">{m.body}</div></div>
              <span className="text-xs text-muted-foreground">{fmtRelative(m.created_at)}</span>
            </Row>
          ))}
        </Section>
      </div>
    </div>
  );
}
